import { useState } from "react";
import { X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ReAnalyzeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (maxPages: number) => void;
  vendorName: string;
  vendorDomain: string;
}

export default function ReAnalyzeModal({
  isOpen,
  onClose,
  onConfirm,
  vendorName,
  vendorDomain,
}: ReAnalyzeModalProps) {
  const [maxPages, setMaxPages] = useState(50);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(Math.min(100, Math.max(1, maxPages)));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#063F6B] to-[#124C77] p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Re-Analyze Vendor</h2>
              <p className="text-sm text-white/70 mt-1">{vendorName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10 min-w-[40px] min-h-[40px] flex items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-5">
          <p className="text-sm text-muted-foreground mb-1">
            The previous analysis for <span className="font-medium text-foreground">{vendorDomain}</span> failed.
          </p>
          <p className="text-sm text-muted-foreground mb-5">
            Configure the max pages to crawl and retry.
          </p>

          <div className="mb-5">
            <label
              htmlFor="max-pages"
              className="block text-sm font-medium text-foreground mb-2"
            >
              Max Pages to Crawl
            </label>
            <input
              id="max-pages"
              type="number"
              min={1}
              max={100}
              value={maxPages}
              onChange={(e) => setMaxPages(Number(e.target.value))}
              className="h-9 w-full px-3 border border-input bg-background text-sm font-medium text-foreground rounded-md shadow-sm transition-colors focus:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Between 1 and 100 pages
            </p>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="brand"
              className="flex-1 flex items-center justify-center gap-2"
              onClick={handleConfirm}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Re-Analyze
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
